import React, { useCallback, useMemo } from 'react';
import ArrayContainer from '../Controls/ArrayContainer/ArrayContainer';
import Button from '../Controls/Button/Button';
import Label from '../Controls/Label/Label';
import Description from '../Controls/Description/Description';
import * as actions from '../../containers/Integrations/integrationsActions';
import * as _ from 'lodash';
import styles from './SSOApps.module.css';
import SSOApp from './SSOApp';
import { SUBCATEGORIES } from '../../common/categories';
import { CUSTOM_VENDOR_SEQUENCE_START, DEFAULT_VENDOR_TO_ID } from '@silverfort-nodelibs/common/utils/consts';
import { useGetTabDisplay } from '../../hooks/lockedFeatures/useTabDisplay';
import { SettingsFeatureLock } from '../LockedFeatures/SettingsFeatureLock';

const { SSO_APPS } = SUBCATEGORIES;

const SSOApps = ({ fields, dispatch, ssoVendors, ssoApplicationToRuleDetails, authorizePermission }) => {
    const existVendorKeys = Object.keys(fields).filter((vendorId) => !_.isNaN(+vendorId));
    const newVendorKeys = Object.keys(fields).filter((vendorId) => _.isNaN(+vendorId));
    const { enabled } = useGetTabDisplay(SSO_APPS.ID);
    const disabled = useMemo(() => !authorizePermission || !enabled, [authorizePermission, enabled]);

    const dispatchAdd = useCallback(
        (arrFieldId, vendorName = '') => {
            dispatch({ type: actions.ADD_SSO_APP, payload: { tabKey: SSO_APPS.ID, arrFieldId, vendorName } });
        },
        [dispatch]
    );

    const dispatchAddNewVendor = useCallback(() => {
        dispatch({ type: actions.ADD_SSO_VENDOR, payload: { tabKey: SSO_APPS.ID } });
    }, [dispatch]);

    const dispatchRemove = useCallback(
        (arrFieldId, index) => {
            dispatch({
                type: actions.REMOVE_SSO_APP,
                payload: { tabKey: SSO_APPS.ID, index, arrFieldId }
            });
        },
        [dispatch]
    );

    const dispatchUpdateArray = useCallback(
        (fieldId, index, arrFieldId, value) => {
            dispatch({
                type: actions.UPDATE_SSO_APP,
                payload: { tabKey: SSO_APPS.ID, fieldId, index, arrFieldId, value }
            });
        },
        [dispatch]
    );

    const dispatchUpdateNewVendor = useCallback(
        (fieldId, index, arrFieldId, value) => {
            dispatch({
                type: actions.UPDATE_SSO_VENDOR,
                payload: { tabKey: SSO_APPS.ID, fieldId, index, arrFieldId, value }
            });
        },
        [dispatch]
    );

    return (
        <>
            <div style={{ padding: 10 }}>
                <Description
                    value="Extend the authentication factors of any cloud SSO provider and SAML IdPs to all your on-premise resources."
                    expendDescription={true}
                />
            </div>

            {existVendorKeys.map((vendor) => {
                const apps = fields[vendor];
                const vendorId = +vendor;
                const vendorName = ssoVendors[vendor].name;

                const isDefaultVendor = vendorId === DEFAULT_VENDOR_TO_ID.DEFAULT;
                const isCustomVendor = vendorId >= CUSTOM_VENDOR_SEQUENCE_START;

                if (isDefaultVendor || (isCustomVendor && apps.value.length === 0)) {
                    return <></>;
                }

                return (
                    <div style={{ padding: 10 }} key={vendorId}>
                        <Label value={vendorName} />
                        {apps.value.map((app, index) => {
                            return (
                                <ArrayContainer
                                    key={vendorId + index}
                                    header={`${vendorName} Application`}
                                    topSpace={true}
                                    isProtected={ssoApplicationToRuleDetails[app.id.value]?.length > 0}
                                    isProtectedMessage="This bridge application is used by an at least one existing policy, please remove the policy and try again."
                                    remove={() => !disabled && dispatchRemove(vendor, index)}>
                                    <SSOApp
                                        app={app}
                                        isVendorFieldDisabled={true}
                                        isVendorFieldVisible={isCustomVendor}
                                        changedFunc={(fId, v) => dispatchUpdateArray(vendor, index, fId, v)}
                                        disabled={disabled}
                                    />
                                </ArrayContainer>
                            );
                        })}
                        <SettingsFeatureLock id={SSO_APPS.ID} isTabId={true}>
                            <Button
                                style={{ marginTop: apps.value.length === 0 ? '20px' : '0' }}
                                onClick={() => dispatchAdd(vendorId, vendorName)}
                                disabled={disabled}>
                                + Add Application
                            </Button>
                        </SettingsFeatureLock>
                    </div>
                );
            })}

            {newVendorKeys.map((vendorId) => {
                const apps = fields[vendorId];
                const vendorName = ssoVendors[vendorId].name;

                if (apps.value.length === 0) {
                    return <></>;
                }

                return (
                    <div style={{ padding: 10 }} key={vendorId}>
                        <Label value={vendorName} />
                        {apps.value.map((app, index) => {
                            return (
                                <ArrayContainer
                                    key={vendorId + index}
                                    header={`${vendorName} Application`}
                                    topSpace={true}
                                    remove={() => !disabled && dispatchRemove(vendorId, index)}>
                                    <SSOApp
                                        app={app}
                                        isVendorFieldDisabled={false}
                                        isVendorFieldVisible={true}
                                        changedFunc={(fId, v) => dispatchUpdateNewVendor(vendorId, index, fId, v)}
                                        disabled={disabled}
                                    />
                                </ArrayContainer>
                            );
                        })}
                        <SettingsFeatureLock id={SSO_APPS.ID} isTabId={true}>
                            <Button
                                style={{ marginTop: apps.value.length === 0 ? '20px' : '0' }}
                                onClick={() => !disabled && dispatchAdd(vendorId, vendorName)}>
                                + Add Application
                            </Button>
                        </SettingsFeatureLock>
                    </div>
                );
            })}
            <div className={styles.horizontalDivider} />
            <div style={{ padding: 10 }}>
                <SettingsFeatureLock id={SSO_APPS.ID} isTabId={true}>
                    <Button className={styles.addButton} onClick={() => dispatchAddNewVendor()} disabled={disabled}>
                        + Add New IdP
                    </Button>
                </SettingsFeatureLock>
            </div>
        </>
    );
};

export default React.memo(SSOApps);
